import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useOrganization } from '../../../context/OrganizationContext';
import { getGSCStatus, initiateGSCOAuth, disconnectGSC, updateGSCSite, getGSCOverviewReport } from '../../../services/externalService';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import toast from 'react-hot-toast';

const GoogleSearchConsole = ({ organizationData, organizationId }) => {
  const location = useLocation();
  const { userRole } = useOrganization();
  const [loading, setLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState(null);
  const [showSiteSelector, setShowSiteSelector] = useState(false);
  const [showTestSection, setShowTestSection] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [showReportDrawer, setShowReportDrawer] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportError, setReportError] = useState(null);

  // Determine permissions based on role
  const isAdmin = userRole === 'owner' || userRole === 'admin';
  const isAnalyst = userRole === 'analyst';
  const isViewer = userRole === 'viewer';
  
  // Permission flags
  const canConnectDisconnect = isAdmin;
  const canChangeSite = isAdmin || isAnalyst;

  // Load GSC integration status on component mount
  useEffect(() => {
    if (organizationId) {
      loadGSCStatus();
    }
  }, [organizationId]);

  // Refresh status when returning from OAuth (detect URL changes)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('gsc_success') === 'true') {
      // Small delay to ensure the backend has saved the data
      setTimeout(() => {
        loadGSCStatus();
      }, 1000);
    }
  }, [location.search]);

  const loadGSCStatus = async () => {
    try {
      const response = await getGSCStatus(organizationId);
      const data = response.data;
      setIsConnected(data.connected);
      if (data.connected) {
        setConnectionInfo(data);
      }
    } catch (error) {
      // Error loading GSC status
      setIsConnected(false);
      setConnectionInfo(null);
    }
  };

  const handleConnect = async () => {
    setLoading(true);
    try {
      const response = await initiateGSCOAuth(organizationId);
      const data = response.data;
      // Redirect to Google OAuth authorization URL
      window.location.href = data.authUrl;
    } catch (error) {
      alert('Failed to connect Google Search Console');
      const errorMessage = error.response?.data?.message || error.message || 'Failed to initiate GSC connection';
      toast.error(errorMessage, {
        duration: 4000,
        position: 'top-right',
      });
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Google Search Console? This will remove all stored tokens and settings.')) {
      return;
    }

    setLoading(true);
    try {
      await disconnectGSC(organizationId);
      setIsConnected(false);
      setConnectionInfo(null);
      setShowSiteSelector(false);
      
      toast.success('Google Search Console disconnected successfully', {
        duration: 3000,
        position: 'top-right',
      });
    } catch (error) {
      alert('Failed to disconnect GSC');
      const errorMessage = error.response?.data?.message || error.message || 'Failed to disconnect Google Search Console';
      toast.error(errorMessage, {
        duration: 4000,
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSiteChange = async (siteUrl, siteDisplayName) => {
    setLoading(true);
    try {
      await updateGSCSite(organizationId, siteUrl, siteDisplayName);
      // Reload status to get updated info
      await loadGSCStatus();
      setShowSiteSelector(false);
      
      toast.success(`GSC site updated to: ${siteDisplayName}`, {
        duration: 3000,
        position: 'top-right',
      });
    } catch (error) {
      alert('Failed to update GSC site');
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update GSC site';
      toast.error(errorMessage, {
        duration: 4000,
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setTestLoading(true);
    setTestResults(null);
    setReportError(null);
    
    try {
      // Fetch real search console data
      const overviewReportResponse = await getGSCOverviewReport(organizationId);
      
      setReportData(overviewReportResponse.data.data);
      
      setTestResults({
        success: true,
        message: 'Connection test successful!',
        details: 'Successfully fetched search performance data from your GSC property'
      });
      
      // Open the drawer to show the report
      setShowReportDrawer(true);
      
      toast.success('GSC connection verified successfully', {
        duration: 3000,
        position: 'top-right',
      });
    } catch (error) {
      alert('Test connection failed');
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch search console data';
      setReportError(errorMessage);
      setTestResults({
        success: false,
        message: 'Connection test failed',
        details: errorMessage
      });
      
      toast.error(errorMessage, {
        duration: 4000,
        position: 'top-right',
      });
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Connection Status Header - Only show when connected */}
      {isConnected && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              {/* Google Search Console Icon */}
              <div className="w-14 h-14 bg-white rounded-lg shadow-sm flex items-center justify-center flex-shrink-0">
                <svg className="w-8 h-8 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/>
                </svg>
              </div>
              
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-xl font-semibold text-gray-900">Google Search Console</h3>
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                    isConnected 
                      ? 'bg-green-100 text-green-700 border border-green-200' 
                      : 'bg-gray-100 text-gray-700 border border-gray-200'
                  }`}>
                    {isConnected ? '● Connected' : '○ Not Connected'}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  {isConnected 
                    ? 'Your Google Search Console property is connected and ready to use'
                    : 'Connect your Google Search Console property to enable search performance tracking'
                  }
                </p>

                {/* Quick Stats when connected */}
                {isConnected && connectionInfo && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div className="bg-white rounded-lg p-3 border border-gray-100">
                      <p className="text-xs text-gray-500 mb-1">Account</p>
                      <p className="text-sm font-medium text-gray-900 truncate">{connectionInfo.email}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-gray-100">
                      <p className="text-xs text-gray-500 mb-1">Connected Since</p>
                      <p className="text-sm font-medium text-gray-900">
                        {new Date(connectionInfo.connectedAt).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric', 
                          year: 'numeric' 
                        })}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-3 border border-gray-100">
                      <p className="text-xs text-gray-500 mb-1">Current Site</p>
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {connectionInfo.selectedSiteDisplayName || 'Not selected'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 ml-4">
              {canConnectDisconnect && (
                <>
                  {isConnected ? (
                    <>
                      <button
                        onClick={loadGSCStatus}
                        disabled={loading}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Refresh
                      </button>
                      <button
                        onClick={handleDisconnect}
                        disabled={loading}
                        className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Disconnecting...' : 'Disconnect'}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={handleConnect}
                      disabled={loading}
                      className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Connecting...' : 'Connect'}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Not Connected State */}
      {!isConnected && (
        <div className="bg-white rounded-xl p-8 border border-gray-200 flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Google Search Console</h3>
          <p className="text-gray-600 mb-6 max-w-md">
            Connect your Google Search Console account to monitor search performance, track impressions, clicks, and average position for your website.
          </p>
          {canConnectDisconnect && (
            <button
              onClick={handleConnect}
              disabled={loading}
              className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Connecting...' : 'Connect Google Search Console'}
            </button>
          )}
          {!canConnectDisconnect && (
            <p className="text-xs text-gray-500">
              Only administrators can connect this integration.
            </p>
          )}
        </div>
      )}

      {/* Test Connection Section - Only show when connected and admin */}
      {isConnected && canConnectDisconnect && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div 
            className="px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => setShowTestSection(!showTestSection)}
          >
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <h4 className="font-semibold text-gray-900">Test Connection</h4>
            </div>
            <svg 
              className={`w-5 h-5 text-gray-400 transition-transform ${showTestSection ? 'transform rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>

          {showTestSection && (
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
              <p className="text-sm text-gray-600 mb-4">
                Test the connection to your Google Search Console account by fetching a sample of your search performance data.
              </p>
              <button
                onClick={handleTestConnection}
                disabled={testLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {testLoading ? 'Testing...' : 'Run Test'}
              </button>

              {testResults && (
                <div className={`mt-4 p-4 rounded-lg border ${
                  testResults.success 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
                }`}>
                  <p className={`font-medium ${testResults.success ? 'text-green-900' : 'text-red-900'}`}>
                    {testResults.message}
                  </p>
                  <p className={`text-sm mt-1 ${testResults.success ? 'text-green-700' : 'text-red-700'}`}>
                    {testResults.details}
                  </p>
                </div>
              )}

              {showReportDrawer && reportData && (
                <div className="mt-6 space-y-6">
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h5 className="font-semibold text-gray-900 mb-4">Search Performance Summary</h5>
                    
                    {reportData.summary && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Clicks</p>
                          <p className="text-2xl font-bold text-gray-900">{reportData.summary.clicks?.toLocaleString() || '0'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Impressions</p>
                          <p className="text-2xl font-bold text-gray-900">{reportData.summary.impressions?.toLocaleString() || '0'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">CTR</p>
                          <p className="text-2xl font-bold text-gray-900">{reportData.summary.ctr ? `${(reportData.summary.ctr * 100).toFixed(2)}%` : '0%'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Avg Position</p>
                          <p className="text-2xl font-bold text-gray-900">{reportData.summary.position?.toFixed(1) || '0'}</p>
                        </div>
                      </div>
                    )}

                    {reportData.site && (
                      <div className="mt-4 p-3 bg-gray-50 rounded border border-gray-200">
                        <p className="text-xs text-gray-500">Site</p>
                        <p className="text-sm font-medium text-gray-900 break-all">{reportData.site.url}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Site Management Section */}
      {isConnected && canChangeSite && connectionInfo && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div 
            className="px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => setShowSiteSelector(!showSiteSelector)}
          >
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.658 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              <h4 className="font-semibold text-gray-900">Manage Sites</h4>
            </div>
            <svg 
              className={`w-5 h-5 text-gray-400 transition-transform ${showSiteSelector ? 'transform rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>

          {showSiteSelector && (
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
              {connectionInfo.sites && connectionInfo.sites.length > 0 ? (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 mb-4">
                    Select which site you want to track from your {connectionInfo.sites.length} available site{connectionInfo.sites.length !== 1 ? 's' : ''}:
                  </p>
                  {connectionInfo.sites.map((site, index) => (
                    <button
                      key={index}
                      onClick={() => handleSiteChange(site.siteUrl, site.siteUrl)}
                      disabled={loading}
                      className={`w-full p-3 text-left rounded-lg border transition-colors ${
                        connectionInfo.selectedSiteUrl === site.siteUrl
                          ? 'bg-blue-50 border-blue-300'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-gray-900 break-all text-sm">{site.siteUrl}</p>
                          <p className="text-xs text-gray-500 mt-1">Permission: {site.permissionLevel}</p>
                        </div>
                        {connectionInfo.selectedSiteUrl === site.siteUrl && (
                          <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-600">No sites available. Please grant access to your Search Console properties.</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Information Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="text-sm text-blue-900">
            <p className="font-semibold mb-1">About Google Search Console Data</p>
            <p className="text-blue-800">
              Search Console data typically takes about 3 days to process and appear in reports. You can view your search performance metrics including clicks, impressions, average position, and more.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleSearchConsole;
